/**
 * Printable A4 documents for "Export PDF": a clean page (title, date, then a table
 * or text sections) rendered in a preview frame and saved through the browser's
 * "Save as PDF", instead of printing the app screen or downloading .txt/.html.
 */

const escapeHtml = (value: string | number) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

interface DocumentHead {
  /** Also the default file name in the Save as PDF dialog. */
  title: string;
  subtitle?: string;
  /** Short facts under the title, e.g. "Filters: Last 30 days". */
  meta?: string[];
}

/** Tables with more columns than this print on a landscape page. */
const PORTRAIT_MAX_COLUMNS = 5;

const styles = (landscape: boolean) => `
  @page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #1f2328; font-size: 11.5pt; line-height: 1.55; background: #fff; }
  .page { max-width: ${landscape ? '273mm' : '186mm'}; margin: 0 auto; padding: 10mm 6mm; }
  header { border-bottom: 2px solid #1f2328; padding-bottom: 10px; margin-bottom: 18px; }
  .brand { font-size: 9.5pt; letter-spacing: .08em; text-transform: uppercase; color: #6a737d; }
  h1 { margin: 4px 0 4px; font-size: 20pt; line-height: 1.25; }
  .subtitle { color: #57606a; font-size: 12pt; }
  .meta { margin-top: 6px; color: #57606a; font-size: 10pt; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5pt; }
  thead { display: table-header-group; }
  th { text-align: left; background: #eef1f4; border-bottom: 2px solid #d0d7de; padding: 9px 10px;
       font-size: 9.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #424a53; }
  td { border-bottom: 1px solid #e4e8ec; padding: 9px 10px; vertical-align: top; overflow-wrap: break-word; word-break: normal; hyphens: none; }
  tbody tr:nth-child(even) td { background: #f8f9fa; }
  td.nowrap, th.nowrap { white-space: nowrap; overflow-wrap: normal; }
  td.num, th.num { text-align: right; }
  tr { page-break-inside: avoid; }
  .count { margin-top: 12px; color: #57606a; font-size: 10pt; }
  section { margin-bottom: 22px; }
  section + section.break { page-break-before: always; }
  h2 { font-size: 15pt; margin: 0 0 10px; }
  .body { white-space: pre-wrap; font-size: 11.5pt; line-height: 1.7; }
  .note { margin-top: 14px; padding: 9px 12px; background: #f6f8fa; border-left: 3px solid #8c959f; font-size: 10.5pt; }
  footer { margin-top: 22px; color: #8c959f; font-size: 9pt; }
`;

/** Short values that read badly when wrapped: amounts, dates, ids. */
const NUMBER = /^[-+]?[\d,]+(\.\d+)?$/;
const SHORT = /^[-+−]?[\d,.:/ ]+[A-Za-z]{0,3}$|^[-+−]?[A-Z]{3} [\d,.]+$|^\d{1,2} [A-Z][a-z]{2} \d{4}/;

function cellClass(value: string | number): string {
  const text = String(value ?? '').trim();
  if (NUMBER.test(text)) return ' class="nowrap num"';
  if (text.length <= 22 && SHORT.test(text)) return ' class="nowrap"';
  return '';
}

function shell(head: DocumentHead, content: string, landscape = false): string {
  const generated = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  const meta = [...(head.meta ?? []), `Generated ${generated}`];
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(head.title)}</title>
<style>${styles(landscape)}</style></head><body><div class="page">
<header><div class="brand">Twiva CPaaS</div><h1>${escapeHtml(head.title)}</h1>
${head.subtitle ? `<div class="subtitle">${escapeHtml(head.subtitle)}</div>` : ''}
<div class="meta">${meta.map(escapeHtml).join(' · ')}</div></header>
${content}
<footer>Twiva CPaaS · cpaas.twiva.com</footer></div></body></html>`;
}

/** A table document, e.g. a list page's rows. */
export function tableDocument(head: DocumentHead, headers: string[], rows: (string | number)[][]): string {
  // A column of numbers is right-aligned, header included.
  const numeric = headers.map((_, index) => rows.length > 0 && rows.every((row) => NUMBER.test(String(row[index] ?? '').trim())));
  const body = rows.length
    ? rows
        .map((row) => `<tr>${row.map((cell, index) => `<td${numeric[index] ? ' class="nowrap num"' : cellClass(cell)}>${escapeHtml(cell)}</td>`).join('')}</tr>`)
        .join('')
    : `<tr><td colspan="${headers.length}">Nothing to show.</td></tr>`;
  return shell(
    head,
    `<table><thead><tr>${headers.map((cell, index) => `<th${numeric[index] ? ' class="num"' : ''}>${escapeHtml(cell)}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table>
<div class="count">${rows.length} row${rows.length === 1 ? '' : 's'}</div>`,
    headers.length > PORTRAIT_MAX_COLUMNS,
  );
}

export interface TextSection {
  heading: string;
  body: string;
  /** A closing line set apart, e.g. the signature status. */
  note?: string;
}

/** A text document, e.g. contracts; each section after the first starts a new page. */
export function textDocument(head: DocumentHead, sections: TextSection[]): string {
  return shell(
    head,
    sections
      .map(
        (section, index) => `<section class="${index > 0 ? 'break' : ''}"><h2>${escapeHtml(section.heading)}</h2>
<div class="body">${escapeHtml(section.body)}</div>${section.note ? `<div class="note">${escapeHtml(section.note)}</div>` : ''}</section>`,
      )
      .join(''),
  );
}

/** Split a CSV payload (as the export menus build it) back into header and rows. */
export function parseCsv(csv: string): { headers: string[]; rows: string[][] } {
  const lines: string[][] = [];
  let cell = '';
  let row: string[] = [];
  let quoted = false;
  const text = csv.replace(/^﻿/, '');
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"') quoted = true;
    else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell);
      lines.push(row);
      row = [];
      cell = '';
    } else cell += char;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    lines.push(row);
  }
  const [headers = [], ...rows] = lines.filter((line) => line.some((value) => value.trim() !== ''));
  return { headers, rows };
}
